/**
 * Scenario Cost Analysis — Phase 1 (buildings) + Phase 2 (transport).
 * All cost views use a unified pathway-difference approach (see pathwayCostModel):
 * - BAU costs: baseline = flat, policy = BAU (state policy)
 * - Local policy costs: baseline = BAU, policy = scenario (state + local)
 * - Total costs: baseline = flat, policy = scenario
 * Same formulas (CAPEX + operating) for every comparison; only the pathway pair differs.
 */

import type { PolicyScenario } from './policyImpactService';
import { getCachedBaseline } from './policyBaselineCache';
import { generatePolicyTimeSeriesData } from './policyTimeSeriesService';
import type { TimeSeriesEmissionData } from './policyTimeSeriesService';
import type { BuildingsBauScenario } from './buildingsBauPathways';
import {
  computeBuildingsCostResults,
  type BuildingsCostResults,
  type BuildingsCostAssumptions,
} from './buildingsCostModel';
import type { EfficiencyLayerOptions } from './buildingsEfficiencyModel';
import {
  computeTransportCostResults,
  type TransportCostResults,
  GASOLINE_PRICE_PER_GALLON_DEFAULT,
} from './transportCostModel';
import { generateFlatBaselineTimeSeries } from './policyTimeSeriesService';
import { getFlatBuildingsPathway } from './buildingsBauPathways';
import { fetchHousingUnitsByYear } from './compactDevelopmentService';
import { supabaseLocationService } from './supabaseLocationService';
import { fetchLatestTotalEstablishmentsForGeofips } from './businessEstablishmentsService';

const ANALYSIS_YEAR_MIN = 2023;
const ANALYSIS_YEAR_MAX = 2045;

/**
 * Compute cumulative emissions reduction (MT) from full time series.
 * Time series totalEmissions are already in metric tons (MT); sum annual (baseline − policy) over 2023–2045.
 */
function computeFullEmissionsReductionMT(
  baselineData: TimeSeriesEmissionData[],
  projectedData: TimeSeriesEmissionData[]
): number {
  const baselineByYear = new Map(baselineData.map((d) => [d.year, d.totalEmissions ?? 0]));
  const projectedByYear = new Map(projectedData.map((d) => [d.year, d.totalEmissions ?? 0]));
  let cumulativeReductionMT = 0;
  for (let y = ANALYSIS_YEAR_MIN; y <= ANALYSIS_YEAR_MAX; y++) {
    const base = baselineByYear.get(y) ?? 0;
    const proj = projectedByYear.get(y) ?? 0;
    cumulativeReductionMT += Math.max(0, base - proj);
  }
  return cumulativeReductionMT;
}

export interface LocationCostResult {
  buildings: BuildingsCostResults;
  transport: TransportCostResults;
  /** Buildings + transport upfront */
  totalUpfrontCost: number;
  /** Buildings + transport cumulative net cost */
  totalCumulativeNetCost: number;
  /** Buildings + transport NPV */
  totalNPV: number;
  totalEmissionsReductionMT: number;
  costPerTon: number | null;
  paybackYear: number | null;
  /** Population from baseline (for per-capita metrics). */
  population?: number;
}

export interface ScenarioCostResults {
  byLocation: Record<string, LocationCostResult>;
}

export interface ScenarioCostOptions {
  buildingsBauScenario?: BuildingsBauScenario;
  buildingsRngPercent?: number;
  buildingsCop?: number;
  emfacVersion?: '2021' | '2025';
  efficiencyLayerOptionsByLocation?: Record<string, EfficiencyLayerOptions | undefined>;
  /** Per-location assumptions (prices + optional buildings overrides). Used by scenario cost. */
  priceOverridesByLocation?: Record<string, Partial<BuildingsCostAssumptions> | undefined>;
  /** Transport: gasoline price ($/gal); overridden by priceOverridesByLocation[loc].gasolinePricePerGallon if present. */
  gasolinePricePerGallon?: number;
  /** Per-location gasoline ($/gal). When set for a location, overrides gasolinePricePerGallon for that location. */
  gasolinePriceByLocation?: Record<string, number>;
  /** Transport: electricity price ($/kWh); falls back to buildings electricity if not set. */
  transportElectricityPricePerKwh?: number;
  /** Transport: ICE fuel economy (mpg). */
  iceMpg?: number;
  /** Transport: EV efficiency (kWh/mile). */
  evKwhPerMile?: number;
  /** Transport: incremental cost per EV ($). */
  evIncrementalCost?: number;
  /** Transport: charger cost per EV ($). */
  chargerCostPerEv?: number;
  /** Buildings/transport: discount rate (0–1). Passed to buildings and transport cost. */
  discountRate?: number;
  /** Cost view: incremental vs BAU (default), BAU costs (state policy vs flat), or total costs (state+local vs flat). */
  costView?: 'incremental_vs_bau' | 'bau_costs' | 'total_costs';
}

/**
 * Compute scenario cost results for the same policy scenario across multiple locations.
 * Phase 1: totals = buildings only. Uses existing cache and generates time series per location.
 */
export async function computeScenarioCostResults(
  params: {
    scenario: PolicyScenario;
    locations: string[];
    options?: ScenarioCostOptions;
  }
): Promise<ScenarioCostResults> {
  const { scenario, locations, options } = params;
  const buildingsBauScenario = options?.buildingsBauScenario ?? 'current_policy';
  const emfacVersion = options?.emfacVersion ?? '2025';
  const costView = options?.costView ?? 'incremental_vs_bau';

  const byLocation: Record<string, LocationCostResult> = {};

  for (const loc of locations) {
    try {
      const timeSeries = await generatePolicyTimeSeriesData(
        loc,
        scenario,
        emfacVersion,
        buildingsBauScenario,
        options?.efficiencyLayerOptionsByLocation?.[loc] ? { efficiencyLayerOptions: options.efficiencyLayerOptionsByLocation[loc] } : undefined
      );

      const cacheKey = `baseline:${loc}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
      const baseline = getCachedBaseline(cacheKey);
      if (!baseline) {
        continue;
      }

      const flatBaselineData = costView !== 'incremental_vs_bau' ? generateFlatBaselineTimeSeries(baseline) : null;
      const flatPathway = costView !== 'incremental_vs_bau' ? getFlatBuildingsPathway(baseline) : undefined;

      let buildingsBaselineData = timeSeries.baselineData;
      let buildingsProjectedData = timeSeries.projectedData;
      let transportBaselineData = timeSeries.baselineData;
      let transportProjectedData = timeSeries.projectedData;
      if (costView === 'bau_costs' && flatBaselineData) {
        buildingsBaselineData = flatBaselineData;
        buildingsProjectedData = timeSeries.baselineData;
        transportBaselineData = flatBaselineData;
        transportProjectedData = timeSeries.baselineData;
      } else if (costView === 'total_costs' && flatBaselineData) {
        buildingsBaselineData = flatBaselineData;
        buildingsProjectedData = timeSeries.projectedData;
        transportBaselineData = flatBaselineData;
        transportProjectedData = timeSeries.projectedData;
      }

      let housingUnitsByYear: Record<number, number> | undefined;
      let commercialEstablishmentCount: number | null | undefined;
      try {
        const geofips = await supabaseLocationService.getGeofips(loc);
        if (geofips != null && Number.isFinite(Number(geofips))) {
          housingUnitsByYear = await fetchHousingUnitsByYear(Number(geofips), 2023, 2045);
          try {
            commercialEstablishmentCount = await fetchLatestTotalEstablishmentsForGeofips(geofips);
          } catch {
            commercialEstablishmentCount = undefined;
          }
        }
      } catch {
        // Housing fetch failed; fall back to population-based estimate
      }
      const buildings = computeBuildingsCostResults({
        location: loc,
        baseline,
        policyScenario: scenario,
        buildingsBauScenario,
        buildingsRngPercentOverride: options?.buildingsRngPercent,
        buildingsCop: options?.buildingsCop,
        efficiencyLayerOptions: options?.efficiencyLayerOptionsByLocation?.[loc],
        emissionsResults: { baselineData: buildingsBaselineData, projectedData: buildingsProjectedData },
        assumptions: options?.priceOverridesByLocation?.[loc],
        referencePathway: costView === 'bau_costs' ? flatPathway : costView === 'total_costs' ? flatPathway : undefined,
        comparisonTarget: costView === 'bau_costs' ? 'bau' : costView === 'total_costs' ? 'policy' : undefined,
        housingUnitsByYear,
        commercialEstablishmentCount: commercialEstablishmentCount ?? undefined,
      });

      const transportScenario =
        costView === 'bau_costs'
          ? { ...scenario, evAdoptionPercent: baseline.currentEVPercent2045 ?? 20, vmtReductionPercent: 0 }
          : scenario;

      const transport = computeTransportCostResults({
        baseline,
        timeSeries: { baselineData: transportBaselineData, projectedData: transportProjectedData },
        scenario: transportScenario,
        options: {
          gasolinePricePerGallon: options?.gasolinePriceByLocation?.[loc] ?? options?.gasolinePricePerGallon ?? GASOLINE_PRICE_PER_GALLON_DEFAULT,
          electricityPricePerKwh: options?.transportElectricityPricePerKwh ?? options?.priceOverridesByLocation?.[loc]?.electricityPricePerKwh,
          discountRate: options?.discountRate,
          iceMpg: options?.iceMpg,
          evKwhPerMile: options?.evKwhPerMile,
          evIncrementalCost: options?.evIncrementalCost,
          chargerCostPerEv: options?.chargerCostPerEv,
          costView,
          emfacVersion,
        },
      });

      const totalUpfrontCost = buildings.costEffectivenessMetrics.upfrontCapitalCost + transport.upfrontCapitalCost;
      const totalCumulativeNetCost = buildings.costEffectivenessMetrics.cumulativeNetCost + transport.cumulativeNetCost;
      const totalNPV = buildings.costEffectivenessMetrics.netPresentValue + transport.netPresentValue;
      const totalEmissionsReductionMT = computeFullEmissionsReductionMT(buildingsBaselineData, buildingsProjectedData);
      const costPerTon =
        totalEmissionsReductionMT > 0 && Number.isFinite(totalCumulativeNetCost)
          ? totalCumulativeNetCost / totalEmissionsReductionMT
          : null;

      // Portfolio payback: first year when combined cumulative <= 0 after we've seen positive combined (real investment to recover).
      let portfolioPaybackYear: number | null = null;
      if (totalCumulativeNetCost <= 0 && totalUpfrontCost > 0) {
        const buildingSeriesByYear = new Map(buildings.cumulativeNetCostSeries.map((r) => [r.year, r.value]));
        const transportSeriesByYear = new Map(transport.cumulativeNetCostSeries.map((r) => [r.year, r.value]));
        const years = buildings.cumulativeNetCostSeries.map((r) => r.year);
        let seenPositive = false;
        for (const year of years) {
          const cumB = buildingSeriesByYear.get(year) ?? 0;
          const cumT = transportSeriesByYear.get(year) ?? 0;
          const sum = cumB + cumT;
          if (sum > 0) seenPositive = true;
          if (sum <= 0 && seenPositive) {
            portfolioPaybackYear = year;
            break;
          }
        }
      }
      const paybackYear = portfolioPaybackYear;

      byLocation[loc] = {
        buildings,
        transport,
        totalUpfrontCost,
        totalCumulativeNetCost,
        totalNPV,
        totalEmissionsReductionMT,
        costPerTon,
        paybackYear,
        population: baseline.population,
      };
    } catch (err) {
      console.error(`Scenario cost failed for ${loc}:`, err);
    }
  }

  return { byLocation };
}

const EMFAC_VERSIONS: ('2021' | '2025')[] = ['2021', '2025'];
const BUILDINGS_BAU_SCENARIOS: BuildingsBauScenario[] = ['conservative', 'current_policy', 'accelerated'];

export interface KeyAssumptionCell {
  emfacVersion: '2021' | '2025';
  buildingsBauScenario: BuildingsBauScenario;
  buildingsUpfrontCost: number;
  transportUpfrontCost: number;
  evPercent2023: number;
  evPercent2045: number;
  /** Building stock electrified share (0–1) in 2045 */
  buildingElectrifiedStock2045: number;
  /** RNG share (0–1) in 2045 */
  rngShare2045: number;
}

/**
 * Fetch key BAU assumptions for the selected location across all 6 combinations:
 * EMFAC 2021 | 2025 × Conservative | Current Policy | Accelerated.
 * Uses costView 'bau_costs' to populate baselines and cost results.
 */
export async function fetchKeyAssumptionsTable(
  location: string,
  scenario: PolicyScenario,
  options?: ScenarioCostOptions
): Promise<KeyAssumptionCell[]> {
  const baseOptions: ScenarioCostOptions = {
    ...options,
    costView: 'bau_costs',
  };
  const key = (e: '2021' | '2025', b: BuildingsBauScenario) => `${e}:${b}`;
  const map = new Map<string, KeyAssumptionCell>();

  const promises = [];
  for (const emfacVersion of EMFAC_VERSIONS) {
    for (const buildingsBauScenario of BUILDINGS_BAU_SCENARIOS) {
      promises.push(
        computeScenarioCostResults({
          scenario,
          locations: [location],
          options: { ...baseOptions, emfacVersion, buildingsBauScenario },
        }).then((results) => {
          const r = results.byLocation[location];
          const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
          const baseline = getCachedBaseline(cacheKey);
          const pathway = baseline?.buildingsBauPathway;
          const idx2045 = pathway?.years?.indexOf(2045) ?? -1;
          map.set(key(emfacVersion, buildingsBauScenario), {
            emfacVersion,
            buildingsBauScenario,
            buildingsUpfrontCost: r?.buildings?.costEffectivenessMetrics?.upfrontCapitalCost ?? 0,
            transportUpfrontCost: r?.transport?.upfrontCapitalCost ?? 0,
            evPercent2023: baseline?.currentEVPercent ?? 0,
            evPercent2045: baseline?.currentEVPercent2045 ?? 0,
            buildingElectrifiedStock2045: idx2045 >= 0 && pathway?.stockElectrifiedByYear ? pathway.stockElectrifiedByYear[idx2045] ?? 0 : 0,
            rngShare2045: idx2045 >= 0 && pathway?.rngShareByYear ? pathway.rngShareByYear[idx2045] ?? 0 : 0,
          });
        })
      );
    }
  }
  await Promise.all(promises);
  const cells: KeyAssumptionCell[] = [];
  for (const e of EMFAC_VERSIONS) {
    for (const b of BUILDINGS_BAU_SCENARIOS) {
      const cell = map.get(key(e, b));
      if (cell) cells.push(cell);
    }
  }
  return cells;
}
